import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { Link, useSearchParams } from 'react-router-dom';
import * as countryCodes from 'country-codes-list';
import { Helmet } from 'react-helmet';
import { withScreen } from './Screen';
import { OrderFiltersBlock } from './OrderFiltersBlock';
import Button from './common/Button';
import { verifyOrderDataIntegrity, VerifyOrderDataIntegrity_Order } from '../../common/VerifyOrderDataIntegrity';
import { useRequestManager } from './common/RequestManager';
import { syncAllLeboncoinOrdersFromFrontend } from '../lib/syncLeboncoinOrderFromFrontend';
import { OrdersFilters } from '../../codegen/graphql';
import { parseIntoURLSearchParams } from '../lib/parseIntoURLSearchParams';

const CHUNK_SIZE = 100;

const GET_ORDERS_FOR_INTEGRITY = gql`
  query VerifyDataIntegrity_GetOrders(
    $filters: OrdersFilters
    $page: Int
    $pageSize: Int
  ) {
    orders(
      filters: $filters
      page: $page
      pageSize: $pageSize
    ) {
      nodes {
        id
        orderId
        shopId
        shop {
          shopType
        }
        ...VerifyOrderDataIntegrity_Order
      }
      total
      hasNextPage
      page
      pageSize
    }
  }
  ${VerifyOrderDataIntegrity_Order}
`;

const RESYNC_EXTERNAL_ORDERS_MUTATION = gql`
  mutation VerifyDataIntegrity_ResyncExternalOrders($extIdsAndShopTypes: [IdAndShopTypeInput!]!) {
    resyncExternalOrders(extIdsAndShopTypes: $extIdsAndShopTypes) {
      process {
        id
      }
    }
  }
`;

const SYNC_SELECTED_ORDERS_FULLFILLMENTS_MUTATION = gql`
  mutation VerifyDataIntegrity_SyncSelectedOrdersFullfillments($orderIds: [ID!]) {
    syncAllOrdersFullfillments(orderIds: $orderIds) {
      successfulCount
      erroredCount
    }
  }
`;

const TRY_FILL_SELECTED_ORDERS_FULLFILLMENTS_MUTATION = gql`
  mutation VerifyDataIntegrity_TryFillSelectedOrdersFullfillments($orderIds: [ID!]!) {
    tryFillFulfillmentsForOrders(orderIds: $orderIds) {
      process {
        id
      }
    }
  }
`;

const TRY_FILL_SELECTED_ORDERS_SHIPPING_COST_MUTATION = gql`
  mutation VerifyDataIntegrity_TryFillSelectedOrdersShippingCost($orderIds: [ID!]!) {
    tryFillShippingCostForOrders(orderIds: $orderIds) {
      process {
        id
      }
    }
  }
`;

type FailedOrder = {
  id: string;
  externalOrderId: string | null;
  shopId: string;
  shopType: string;
  errors: string[];
};

function VerifyDataIntegrityPage() {
  const client = useApolloClient();
  const { trackRequest } = useRequestManager();
  const [searchParams, setSearchParams] = useSearchParams();
  const runRef = useRef(0);

  const [isDateFiltersOpen, setIsDateFiltersOpen] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [verifiedCount, setVerifiedCount] = useState(0);
  const [successfulCount, setSuccessfulCount] = useState(0);
  const [erroredCount, setErroredCount] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [failedOrders, setFailedOrders] = useState<FailedOrder[]>([]);
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<string>>(new Set());

  const [resyncExternalOrdersMutation, { loading: isResyncing }] = useMutation(RESYNC_EXTERNAL_ORDERS_MUTATION);
  const [syncSelectedOrdersFullfillmentsMutation, { loading: isSyncingFulfillments }] = useMutation(SYNC_SELECTED_ORDERS_FULLFILLMENTS_MUTATION);
  const [tryFillSelectedOrdersFullfillmentsMutation, { loading: isTryingFillFulfillments }] = useMutation(TRY_FILL_SELECTED_ORDERS_FULLFILLMENTS_MUTATION);
  const [tryFillSelectedOrdersShippingCostMutation, { loading: isTryingFillShippingCost }] = useMutation(TRY_FILL_SELECTED_ORDERS_SHIPPING_COST_MUTATION);

  const currentFilters: OrdersFilters = useMemo(() => ({
    search: searchParams.get('search'),
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate'),
    countryCode: searchParams.get('countryCode'),
    adId: searchParams.get('adId'),
    variationId: searchParams.get('variationId'),
  }), [searchParams]);

  const formatError = (error: ReturnType<typeof verifyOrderDataIntegrity>[number]) => {
    const debugValues = error.debug && typeof error.debug === 'object'
      ? Object.values(error.debug).filter(Boolean).map((value) => String(value))
      : [];

    if (!debugValues.length) {
      return error.title;
    }

    return `${error.title} (${debugValues.join(' / ')})`;
  };

  const runVerification = useCallback(async () => {
    const currentRunId = runRef.current + 1;
    runRef.current = currentRunId;

    setIsRunning(true);
    setVerifiedCount(0);
    setSuccessfulCount(0);
    setErroredCount(0);
    setTotalOrders(0);
    setFailedOrders([]);
    setSelectedOrderIds(new Set());

    let page = 1;
    let localVerifiedCount = 0;
    let localSuccessfulCount = 0;
    let localErroredCount = 0;

    try {
      while (true) {
        const result = await client.query({
          query: GET_ORDERS_FOR_INTEGRITY,
          fetchPolicy: 'network-only',
          variables: {
            filters: {
              search: currentFilters.search,
              startDate: currentFilters.startDate,
              endDate: currentFilters.endDate,
              countryCode: currentFilters.countryCode,
              adId: currentFilters.adId,
              variationId: currentFilters.variationId,
            },
            page,
            pageSize: CHUNK_SIZE,
          },
        });

        if (runRef.current !== currentRunId) {
          return;
        }

        const connection = result.data.orders;
        const chunkFailedOrders: FailedOrder[] = [];

        setTotalOrders(connection.total);

        for (const order of connection.nodes) {
          const orderChecks = verifyOrderDataIntegrity(order);
          const failedChecks = orderChecks.filter((check) => !check.result);
          const uniqueErrors = Array.from(new Set(failedChecks.map(formatError)));

          localVerifiedCount += 1;
          if (uniqueErrors.length > 0) {
            localErroredCount += 1;
            chunkFailedOrders.push({
              id: String(order.id),
              externalOrderId: order.orderId || null,
              shopId: order.shopId,
              shopType: order.shop.shopType,
              errors: uniqueErrors,
            });
          } else {
            localSuccessfulCount += 1;
          }
        }

        setVerifiedCount(localVerifiedCount);
        setSuccessfulCount(localSuccessfulCount);
        setErroredCount(localErroredCount);
        setFailedOrders((previous) => [...previous, ...chunkFailedOrders]);

        const isLastPage = (connection.page * connection.pageSize) >= connection.total;
        if (isLastPage) {
          break;
        }

        page += 1;
      }
    } finally {
      if (runRef.current === currentRunId) {
        setIsRunning(false);
      }
    }
  }, [client, currentFilters]);


  const handleFiltersChange = useCallback((change: Partial<OrdersFilters>) => {
    setSearchParams(parseIntoURLSearchParams(change));
  }, [setSearchParams]);

  const selectedFailedOrders = failedOrders.filter((order) => selectedOrderIds.has(order.id));
  const allRowsSelected = failedOrders.length > 0 && selectedOrderIds.size === failedOrders.length;

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrderIds((previous) => {
      const next = new Set(previous);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const toggleAllRowsSelection = () => {
    if (allRowsSelected) {
      setSelectedOrderIds(new Set());
      return;
    }
    setSelectedOrderIds(new Set(failedOrders.map((order) => order.id)));
  };

  const resyncExternalOrders = useCallback(async () => {
    // Collect leboncoin and other orders in a reduce function
    const { leboncoinOrders, otherOrders } = selectedFailedOrders.reduce(
      (acc, order) => {
        if (!!order.externalOrderId) {
          if (order.shopType === 'leboncoin') {
            acc.leboncoinOrders.push(order);
          } else {
            acc.otherOrders.push({
              id: order.externalOrderId!,
              shopId: order.shopId,
              shopType: order.shopType,
            });
          }
        }
        return acc;
      },
      { leboncoinOrders: [] as typeof selectedFailedOrders, otherOrders: [] as { id: string; shopId: string; shopType: string }[] }
    );

    const promises = [];
    // Only call mutation for non-leboncoin orders as before
    if (otherOrders.length > 0) {
      promises.push(trackRequest(
        resyncExternalOrdersMutation({
          variables: {
            extIdsAndShopTypes: otherOrders.map((order) => ({
              id: order.id,
              shopId: order.shopId,
              shopType: order.shopType,
            })),
          },
        }),
        {
          title: 'Resyncing external orders',
          isBlocking: false,
        }
      ));
    }

    if (leboncoinOrders.length > 0) {
      promises.push(syncAllLeboncoinOrdersFromFrontend(leboncoinOrders.map((order) => ({
        ourOrderId: order.id,
        externalOrderId: order.externalOrderId!,
      }))));
    }

    await Promise.all(promises);
  }, [selectedFailedOrders, resyncExternalOrdersMutation, trackRequest]);

  const syncSelectedOrdersFulfillments = useCallback(async () => {
    if (!selectedOrderIds.size) {
      return;
    }

    await trackRequest(
      syncSelectedOrdersFullfillmentsMutation({
        variables: {
          orderIds: Array.from(selectedOrderIds),
        },
      }),
      {
        title: 'Syncing selected orders fulfillments',
        isBlocking: false,
      }
    );
  }, [selectedOrderIds, syncSelectedOrdersFullfillmentsMutation, trackRequest]);

  const tryFillSelectedOrdersFulfillments = useCallback(async () => {
    if (!selectedOrderIds.size) {
      return;
    }

    await trackRequest(
      tryFillSelectedOrdersFullfillmentsMutation({
        variables: {
          orderIds: Array.from(selectedOrderIds),
        },
      }),
      {
        title: 'Trying to fill selected orders fulfillments',
        isBlocking: false,
      }
    );
  }, [selectedOrderIds, tryFillSelectedOrdersFullfillmentsMutation, trackRequest]);

  const tryFillSelectedOrdersShippingCost = useCallback(async () => {
    if (!selectedOrderIds.size) {
      return;
    }

    await trackRequest(
      tryFillSelectedOrdersShippingCostMutation({
        variables: {
          orderIds: Array.from(selectedOrderIds),
        },
      }),
      {
        title: 'Trying to fill selected orders shipping cost',
        isBlocking: false,
      }
    );
  }, [selectedOrderIds, tryFillSelectedOrdersShippingCostMutation, trackRequest]);

  return (
    <>
      <Helmet>
        <title>Verify Data Integrity</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify Data Integrity</h1>
        <p className="text-gray-600 mb-4">
          Run checks on filtered orders in chunks of {CHUNK_SIZE}.
        </p>

        <OrderFiltersBlock
          currentFilters={currentFilters}
          onFiltersChange={handleFiltersChange}
        />

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <Button onClick={runVerification} disabled={isRunning}>
            {isRunning ? 'Running...' : 'Run Verification'}
          </Button>
          {isRunning && (
            <span className="text-sm text-gray-600">
              Processed {verifiedCount}{totalOrders ? ` / ${totalOrders}` : ''}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="rounded-lg border bg-white p-4">
            <div className="text-sm text-gray-500">Verified Orders</div>
            <div className="text-2xl font-bold text-gray-900">{verifiedCount}</div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="text-sm text-gray-500">Successful</div>
            <div className="text-2xl font-bold text-green-700">{successfulCount}</div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="text-sm text-gray-500">Errors</div>
            <div className="text-2xl font-bold text-red-700">{erroredCount}</div>
          </div>
        </div>

        {selectedOrderIds.size > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <span className="text-sm text-gray-700">{selectedOrderIds.size} selected</span>
            <Button onClick={resyncExternalOrders} disabled={isResyncing}>
              {isResyncing ? 'Resyncing...' : 'Resync external orders'}
            </Button>
            <Button onClick={syncSelectedOrdersFulfillments} disabled={isSyncingFulfillments} preset="white">
              {isSyncingFulfillments ? 'Syncing...' : 'Sync all fulfillments'}
            </Button>
            <Button onClick={tryFillSelectedOrdersFulfillments} disabled={isTryingFillFulfillments} preset="white">
              {isTryingFillFulfillments ? 'Trying...' : 'Try fill fulfillments'}
            </Button>
            <Button onClick={tryFillSelectedOrdersShippingCost} disabled={isTryingFillShippingCost} preset="white">
              {isTryingFillShippingCost ? 'Trying...' : 'Try fill all shipping cost'}
            </Button>
          </div>
        )}

        <div className="overflow-x-auto bg-white rounded-lg shadow-sm border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={allRowsSelected}
                    onChange={toggleAllRowsSelection}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Errors
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {failedOrders.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-gray-500">
                    {verifiedCount === 0
                      ? 'Run verification to list integrity errors.'
                      : 'No integrity errors found for current filters.'}
                  </td>
                </tr>
              ) : (
                failedOrders.map((order) => (
                  <tr key={`${order.shopType}:${order.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 align-top">
                      <input
                        type="checkbox"
                        checked={selectedOrderIds.has(order.id)}
                        onChange={() => toggleOrderSelection(order.id)}
                      />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium">
                        <Link to={`/orders/${order.id}`} className="text-blue-600 hover:underline">
                          {order.shopType} #{order.externalOrderId || order.id}
                        </Link>
                      </div>
                      <div className="text-xs text-gray-500">Internal ID: {order.id}</div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <ul className="list-disc ml-5 space-y-1">
                        {order.errors.map((error, index) => (
                          <li key={index} className="text-sm text-red-700">{error}</li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default withScreen(VerifyDataIntegrityPage);
